// src/quality-management/utils/overtimeCalculator.ts

import { Task, OvertimeType } from '../services/api/types';

// Constants
export const WEEKLY_HOUR_LIMIT = 44;
export const BASE_MONTHLY_SALARY = 5000; // R$5000
export const MONTHLY_WORK_HOURS = (44 * 52) / 12; // ~190.67 hours/month
export const HOURLY_RATE = BASE_MONTHLY_SALARY / MONTHLY_WORK_HOURS;

/**
 * Calculate total hours for tasks in the same week as the given date
 */
export function calculateWeeklyHours(tasks: Task[], targetDate: Date): number {
  const weekStart = getWeekStart(targetDate);
  const weekEnd = getWeekEnd(targetDate);
  
  const weeklyTasks = tasks.filter(task => {
    const taskDate = new Date(task.task_date + 'T00:00:00');
    return taskDate >= weekStart && taskDate <= weekEnd;
  });
  
  return weeklyTasks.reduce((total, task) => total + (task.expected_hours || 0), 0);
}

/**
 * Calculate overtime hours if adding a new task would exceed 44h/week
 */
export function calculateOvertimeHours(
  currentWeeklyHours: number, 
  newTaskHours: number
): number {
  const totalHours = currentWeeklyHours + newTaskHours;
  return Math.max(0, totalHours - WEEKLY_HOUR_LIMIT);
}

/**
 * Calculate overtime pay based on type and hours
 */
export function calculateOvertimePay(overtimeHours: number, overtimeType: OvertimeType): number {
  if (overtimeHours <= 0) return 0;
  
  let rateMultiplier = 1.5; // Default to regular overtime
  
  switch (overtimeType) {
    case 'regular_overtime':
      rateMultiplier = 1.5; // +50%
      break;
    case 'weekend_holiday':
      rateMultiplier = 2.0; // +100%
      break;
    case 'night_work':
      rateMultiplier = 1.7; // +20% night + 50% OT = 1.7
      break;
    case 'travel_time':
      rateMultiplier = 1.5; // +50%
      break;
    case 'hazardous_work':
      rateMultiplier = 1.25; // +25% (middle of +10% to +40% range)
      break;
    case 'intermittent_work':
      rateMultiplier = 1.5; // +50%
      break;
    case 'weekly_rest_dsr':
      rateMultiplier = 1.5; // Indirect increase, using standard OT
      break;
    default:
      rateMultiplier = 1.5;
  }
  
  const overtimeHourlyRate = HOURLY_RATE * rateMultiplier;
  return Math.round(overtimeHours * overtimeHourlyRate * 100) / 100; // Round to 2 decimals
}

/**
 * Get overtime type display names
 */
export function getOvertimeTypeDisplayName(overtimeType: OvertimeType): string {
  const names: Record<OvertimeType, string> = {
    'regular_overtime': 'Regular Overtime (+50%)',
    'weekend_holiday': 'Weekend/Public Holiday (+100%)',
    'night_work': 'Night Work 10 PM - 5 AM (+70%)',
    'weekly_rest_dsr': 'Weekly Paid Rest (DSR)',
    'travel_time': 'Travel Time (+50%)',
    'hazardous_work': 'Hazardous/Unhealthy Work (+25%)',
    'intermittent_work': 'Intermittent Work (+50%)'
  };
  
  return names[overtimeType] || 'Unknown Overtime Type';
}

/**
 * Get all overtime type options for dropdown
 */
export function getOvertimeTypeOptions(): Array<{value: OvertimeType, label: string}> {
  const types: OvertimeType[] = [
    'regular_overtime',
    'weekend_holiday', 
    'night_work',
    'weekly_rest_dsr',
    'travel_time',
    'hazardous_work',
    'intermittent_work'
  ];
  
  return types.map(type => ({
    value: type,
    label: getOvertimeTypeDisplayName(type)
  }));
}

/**
 * Helper functions for date calculations
 */
function getWeekStart(date: Date): Date {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Adjust when day is Sunday
  return new Date(d.setDate(diff));
}

function getWeekEnd(date: Date): Date {
  const weekStart = getWeekStart(date);
  return new Date(weekStart.getTime() + 6 * 24 * 60 * 60 * 1000);
}